import { ConditionAfter } from './conditions/after.condition';
import { ConditionBefore } from './conditions/before.condition';
import { ConditionBlank } from './conditions/blank.condition';
import { ConditionEquals } from './conditions/equals.condition';
import { ConditionHasSome } from './conditions/has-some.condition';
import { ConditionMax } from './conditions/max.condition';
import { ConditionMin } from './conditions/min.condition';
import { ConditionNegate } from './conditions/negate.condition';
import { ConditionOneOf } from './conditions/one-of.condition';
import { ConditionTimespan } from './conditions/timespan.condition';

export * from './conditions/after.condition';
export * from './conditions/before.condition';
export * from './conditions/blank.condition';
export * from './conditions/equals.condition';
export * from './conditions/has-some.condition';
export * from './conditions/max.condition';
export * from './conditions/min.condition';
export * from './conditions/negate.condition';
export * from './conditions/one-of.condition';
export * from './conditions/timespan.condition';

export interface IConditionBase {
  /**
   * The key of the related field
   */
  readonly key: string;
}
/**
 * Any non-connectable condition definitions
 */
type AtomicCondition =
  | ConditionAfter
  | ConditionBefore
  | ConditionBlank
  | ConditionEquals
  | ConditionHasSome
  | ConditionMax
  | ConditionMin
  | ConditionOneOf
  | ConditionTimespan;

/**
 * Condition definitions that might be used
 * together with other conditions
 */
type HigherOrderCondition = Partial<ConditionNegate> & IConditionBase;

/**
 * Condition definitions that connect multiple
 * other condition definitions.
 */
type ConnectiveCondition = ConditionAnd | ConditionOr;

/**
 * Definition for a condition
 */
export type ConditionDefinition =
  AtomicCondition | HigherOrderCondition | ConnectiveCondition;

export interface ConditionAnd {
  /**
   * Array of condition definitions
   */
  readonly and: readonly ConditionDefinition[];
}

export interface ConditionOr {
  /**
   * Array of condition definitions
   */
  readonly or: readonly ConditionDefinition[];
}

/**
 * Type guard that checks if an object fulfills the `ConditionAnd` interface
 *
 * @param c Non nullish object
 * @returns `true` if `c` satisfies `ConditionAnd`
 */
export function isConditionAnd(c: Record<string, any>): c is ConditionAnd {
  return Array.isArray(c['and']);
}

/**
 * Type guard that checks if an object fulfills the `ConditionOr` interface
 *
 * @param c Non nullish object
 * @returns `true` if `c` satisfies `ConditionOr`
 */
export function isConditionOr(c: Record<string, any>): c is ConditionOr {
  return Array.isArray(c['or']);
}

/**
 * Type guard that checks if an object contains atomic condition
 *
 * @param c Non nullish object
 * @returns `true` if `c` satisfies `AtomicCondition`
 */
export function isAtomicCondition(
  c: Record<string, any>,
): c is AtomicCondition {
  return 'key' in c;
}
